<template>
	<div>
		<DateTimePicker
			v-model="selection"
			:min-date="new Date()"
			mode="dateTime"
			is24hr
			:popover="{
				placement: 'top',
				positionFixed: true
			}"
			:valid-hours="validHours"
			:minute-increment="15"
			@popoverDidHide="dateSelected"
		>
			<template #default="{ inputValue, togglePopover, hidePopover }">
				<div class="dates-list mt-2 mb-3">
					<span v-if="potentialDates.length > 0" class="dates-list--chosen">
						<span
							v-for="(potentialDate) in potentialDates"
							:key="potentialDate.id"
							:class="{'has-errors': !isValid(potentialDate)}"
						>
							<span>
								{{ potentialDate.date | dateByObject }}, {{ potentialDate.time | timeByObject }} Uhr
							</span>
							<a href="#" @click.prevent="editDate($event, potentialDate, togglePopover)">
								<Icon icon="edit"/>
							</a>
							<a href="#" @click.prevent="removeDate(potentialDate)">
								<Icon icon="close"/>
							</a>
						</span>
					</span>
					<span class="dates-list--button">
						<a href="#" @click="togglePopover({ ref: $event.target })">
							<Icon icon="plus-circle"/>
							Terminvorschlag hinzufügen
						</a>
					</span>
				</div>
				<div v-if="error" :class="['my-1', 'error', {'font-size-big': error.big }]">{{ error.message }}</div>
			</template>
		</DateTimePicker>
	</div>
</template>

<script>
import DateTimePicker from 'v-calendar/lib/components/date-picker.umd'
import Button from "@/vue/common/Button.vue";
import Icon from "@/vue/icon/Icon.vue";
import {isDefined} from "@/helper/Value";
import {DateTime} from "luxon";
import _ from "lodash";
import {uuid} from "vue-uuid";
import {getDateTimeComparisonValue} from "@/helper/Date";

export default {

	components: {
		Icon,
		Button,
		DateTimePicker,
	},

	props: {
		value: {
			type: Array,
			required: false,
		},
		maxTime: {
			type: Object,
			required: false,
		},
		emptyErrorMessage: {
			type: String,
			required: false,
			default: 'Bitte gib mindestens eine Terminmöglichkeit ein'
		}
	},

	data ()
	{
		return {
			potentialDateToEdit: undefined,
			potentialDates: this.value || [],
			selection: undefined,
		};
	},

	computed: {
		validHours ()
		{
			return {
				min: 0,
				max: this.maxTime ? this.maxTime.hour : 23
			};
		},
		error ()
		{
			if (this.potentialDates.length === 0)
			{
				return {
					message: this.emptyErrorMessage
				};
			}

			const validItems = _.filter(this.potentialDates, pd => this.isValid(pd));

			if (validItems.length !== this.potentialDates.length)
			{
				let error = 'Termine dürfen nicht in der Vergangenheit liegen';

				if (this.maxTime)
				{
					const maxTimeHour = _.padStart(this.maxTime.hour, 2, 0);
					const maxTimeMinute = _.padStart(this.maxTime.minute, 2, 0);

					error += ' und nicht nach ' + maxTimeHour + ':' + maxTimeMinute + ' Uhr nach stattfinden';
				}

				return {
					message: error,
					big: true
				};
			}

			return null;
		},
		valid ()
		{
			return !this.error;
		}
	},

	methods: {
		isValid (potentialDate)
		{
			const now = DateTime.now();
			const nowComparison = getDateTimeComparisonValue(now);

			const dateTimeObj = this.potentialDateToDateTimeObject(potentialDate);
			const dateTimeComparison = getDateTimeComparisonValue(dateTimeObj);

			if (dateTimeComparison < nowComparison)
			{
				return false;
			}

			if (this.maxTime)
			{
				if (dateTimeObj.hour < this.maxTime.hour)
				{
					return true;
				}

				if (dateTimeObj.hour > this.maxTime.hour)
				{
					return false;
				}

				return dateTimeObj.minute <= this.maxTime.minute;
			}

			return true;
		},
		dateSelected ()
		{
			if (!this.selection)
			{
				return;
			}

			const dateObj = DateTime.fromJSDate(this.selection);

			const date = {
				day: dateObj.day,
				month: dateObj.month,
				year: dateObj.year
			};

			const time = {
				hour: dateObj.hour,
				minute: dateObj.minute
			};

			if (isDefined(this.potentialDateToEdit))
			{
				const indexToEdit = _.findIndex(
					this.potentialDates,
					pd => pd.id === this.potentialDateToEdit.id
				);

				this.potentialDates[indexToEdit].date = date;
				this.potentialDates[indexToEdit].time = time;

				this.potentialDates = _.cloneDeep(this.potentialDates);

				this.potentialDateToEdit = undefined;
			}
			else
			{
				this.potentialDates.push({
					id: uuid.v4(),
					date,
					time,
					valid: true,
				});
			}

			this.potentialDates.sort((a, b) =>
			{
				const compareA = getDateTimeComparisonValue(this.potentialDateToDateTimeObject(a));
				const compareB = getDateTimeComparisonValue(this.potentialDateToDateTimeObject(b));

				return compareA === compareB
					? 0
					: (compareA < compareB ? -1 : 1);
			});

			this.potentialDates = _.cloneDeep(this.potentialDates);

			this.emitChangeEvent();

			this.selection = undefined;
		},
		editDate (e, potentialDate, toggle)
		{
			this.potentialDateToEdit = potentialDate;

			const dateTime = this.potentialDateToDateTimeObject(potentialDate);

			this.selection = dateTime.toJSDate();

			toggle({ref: e.target});
		},
		removeDate (potentialDate)
		{
			this.potentialDates = _.filter(this.potentialDates, pd => pd.id !== potentialDate.id);

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			this.$emit('input', this.potentialDates);
		},
		potentialDateToDateTimeObject (potentialDate)
		{
			return DateTime.fromObject({
				day: potentialDate.date.day,
				month: potentialDate.date.month,
				year: potentialDate.date.year,
				hour: potentialDate.time.hour,
				minute: potentialDate.time.minute,
				second: 0,
			});
		}
		/*addDate ()
		{
			this.dates.push({
				date: new Date(),
			});
s
			this.$nextTick(() =>
			{
				const btn = this.$refs.button[this.$refs.button.length - 1];
				btn.click();
			});
		},
		removeDate (date, hide)
		{
			this.dates = this.dates.filter((d) => d !== date);
			hide();
		},
		dateSelected (e, date, toggle)
		{
			this.selected = date;
			toggle({ref: e.target});
		},*/
	},
}
</script>
